// Stable reason codes for every time a board leaves its fast load path, so the
// fallback rate per reason can be counted and driven down — docs/code/shared.md § board fallbacks.

import * as Sentry from '@sentry/react';

/** How far down the ladder the board went: re-read the network instead of the
 *  paint snapshot, re-ingest into the same OPFS store, rebuild the OPFS files, or
 *  keep the painted in-memory store as the engine. */
export type BoardFallbackRung = 'network' | 'reingest' | 'rebuild' | 'memory';

export type BoardFallbackReason =
  // network — the IndexedDB paint snapshot could not be used
  | 'snapshot_unreadable'
  | 'snapshot_undecodable'
  // reingest — last session's OPFS tables not trusted; the fetched projection is ingested again
  | 'empty_store'
  | 'partial_write'
  | 'wire_version_mismatch'
  | 'store_version_mismatch'
  | 'probe_failed'
  | 'sanity_mismatch'
  | 'meta_invalid'
  | 'store_behind_snapshot'
  | 'legacy_pending_edit'
  // rebuild — this board's OPFS files discarded and the store rebuilt
  | 'opfs_open_failed'
  | 'opfs_schema_invalid'
  | 'opfs_commit_failed'
  | 'opfs_ingest_failed'
  | 'opfs_boot_failed'
  // memory — the painted in-memory store kept as the engine
  | 'duckdb_unavailable'
  | 'duckdb_ingest_failed'
  | 'duckdb_timeout'
  | 'opfs_rebuild_failed';

export interface BoardFallback {
  board: string;
  rung: BoardFallbackRung;
  reason: BoardFallbackReason;
}

export interface BoardFallbackDetail {
  /** Human-readable account for DevTools (the stable code always comes first). */
  message?: string;
  /** The failure that forced the fallback, if any. */
  error?: unknown;
  context?: Record<string, unknown>;
}

/** Count one fallback: a GlitchTip issue per `rung:reason` (explicit fingerprint,
 *  so the underlying error text can never split or merge the count) and a
 *  console warning for live triage. */
export function reportBoardFallback(fallback: BoardFallback, detail: BoardFallbackDetail = {}): void {
  const code = `${fallback.rung}:${fallback.reason}`;
  const error = detail.error === undefined ? undefined : errorText(detail.error);
  console.warn(`[board-fallback] ${code}${detail.message ? ` — ${detail.message}` : ''}`, {
    board: fallback.board,
    ...(error === undefined ? {} : { error }),
    ...detail.context,
  });
  Sentry.captureMessage(`board-fallback:${code}`, {
    level: 'warning',
    fingerprint: ['board-fallback', fallback.rung, fallback.reason],
    tags: { board: fallback.board, board_fallback: code },
    extra: { ...(error === undefined ? {} : { error }), ...detail.context },
  });
}

/** How a board load reached an editable engine: the fast path, or the lowest rung it fell to. */
export type BoardLoadPath = 'fast' | BoardFallbackRung;

/** `start` when a load begins, `ready` when it reaches an editable engine. Starts without
 *  a ready are unfinished attempts (hangs AND tabs closed mid-load), not failures. */
export type BoardLoad =
  | { phase: 'start'; board: string; loadId: string }
  | {
      phase: 'ready';
      board: string;
      loadId: string;
      path: BoardLoadPath;
      /** Painted from the IndexedDB snapshot (warm) or the network (cold). */
      paint: 'snapshot' | 'network';
      /** The engine's actual persistence mode: `opfs-owner` is the OPFS fast path. */
      persistence: string;
      msToEngine: number;
    };

/** The denominator for the fallback rate: one event when a load starts and one when it
 *  reaches an editable engine, joined by `loadId` (docs/code/shared.md § board fallbacks). */
export function reportBoardLoad(load: BoardLoad): void {
  const ready = load.phase === 'ready'
    ? { board_load_path: load.path, board_load_paint: load.paint, board_persistence: load.persistence }
    : {};
  Sentry.captureMessage('board-load', {
    level: 'info',
    fingerprint: ['board-load'],
    tags: { board: load.board, board_load_phase: load.phase, ...ready },
    extra: { loadId: load.loadId, ...(load.phase === 'ready' ? { msToEngine: load.msToEngine } : {}) },
  });
}

type Integration = ReturnType<typeof Sentry.getDefaultIntegrations>[number];

/** Sentry's default Dedupe drops an event identical to the previous one, which would
 *  undercount a fallback repeated in one page session. Board counts bypass it; every
 *  other event is still deduped. Found by Codex in the bake-off (X2). */
export function keepBoardCountsFromDedupe(integrations: Integration[]): Integration[] {
  return integrations.map((integration) => {
    const processEvent = integration.processEvent;
    if (integration.name !== 'Dedupe' || !processEvent) return integration;
    return {
      ...integration,
      processEvent(event, hint, client) {
        if (event.message === 'board-load' || event.message?.startsWith('board-fallback:')) return event;
        return processEvent.call(integration, event, hint, client);
      },
    };
  });
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
